import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/auth/auth-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useUpdateServer } from '@/hooks/use-update-server';

const MAX_HOSTNAME = 255;

// Mirrors the backend SetDnsHostnameDto regex — RFC-1123-ish.
const HOSTNAME_RE =
  /^(?=.{1,253}$)([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Inline DNS hostname editor on the server detail panel.
 *
 * Surfaces the `dns_hostname` column added in planet-proxy PR #63. When set,
 * POST /proxy/session returns this hostname to the browser extension so its
 * TLS validation matches the proxy-ingress nginx cert (issued for the
 * hostname, not the IP). When NULL the backend falls back to `public_ip`,
 * which fails SAN validation — so this field is mandatory for any TLS-fronted
 * server (proxy-ingress in particular).
 *
 * PATCH /api/admin/fleet/servers/:name. `support`-role users see the value
 * but the Edit button is disabled (API enforces RBAC regardless).
 */
export function DnsHostnameEditor({
  serverName,
  dnsHostname,
}: {
  serverName: string;
  dnsHostname: string | null;
}): JSX.Element {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(dnsHostname ?? '');
  // The last value persisted by this editor — wins over the prop until the
  // parent detail query re-renders with the server truth.
  const [saved, setSaved] = useState<string | null>(null);
  const mutation = useUpdateServer();

  const current = saved ?? dnsHostname;
  const trimmed = draft.trim();
  const tooLong = trimmed.length > MAX_HOSTNAME;
  const invalid = trimmed.length > 0 && !HOSTNAME_RE.test(trimmed);
  // Allow clearing only by an explicit empty submit; otherwise valid value.
  const canSave = !mutation.isPending && !tooLong && (trimmed.length === 0 || !invalid);

  function startEdit(): void {
    setDraft(current ?? '');
    setEditing(true);
  }

  function save(): void {
    mutation.mutate(
      { name: serverName, patch: { dns_hostname: trimmed } },
      {
        onSuccess: () => {
          toast.success(trimmed.length > 0 ? 'DNS hostname saved.' : 'DNS hostname cleared.');
          setSaved(trimmed.length > 0 ? trimmed : '');
          setEditing(false);
        },
        onError: (err) => toast.error(`Couldn't save DNS hostname: ${err.message}`),
      },
    );
  }

  if (!editing) {
    return (
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">DNS hostname</span>
          {isAdmin ? (
            <Button variant="ghost" size="sm" onClick={startEdit}>
              {current ? 'Edit' : 'Set'}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="ghost" size="sm" disabled>
                    Edit
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Read-only: admin role required</TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className="text-sm">
          {current ? (
            <code className="rounded bg-muted px-1 py-0.5 text-xs">{current}</code>
          ) : (
            <span className="text-muted-foreground">Not set — /proxy/session falls back to public IP.</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">DNS hostname</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {draft.length}/{MAX_HOSTNAME}
        </span>
      </div>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="proxy-us.getpocketvpn.com"
        autoFocus
        aria-label="Server DNS hostname"
      />
      {tooLong && (
        <p className="mt-1 text-xs text-destructive">Hostname must be 255 characters or fewer.</p>
      )}
      {!tooLong && invalid && (
        <p className="mt-1 text-xs text-destructive">Not a valid DNS hostname.</p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={!canSave}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
